import { useEffect, useMemo, useState } from "react";
import { filterCameras, sortCameras } from "./cameras.js";
import { formatKm } from "./format.js";
import { Icon } from "./ui.jsx";

const REFRESH_MS = 120000;

function timeLabel(date) {
  return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

export default function CameraPanel({ cameras, selected, onSelect, onRetry, loading, error, reference, referenceLabel }) {
  const [query, setQuery] = useState("");
  const [imageVersion, setImageVersion] = useState(() => Date.now());
  const [imageState, setImageState] = useState("loading");
  const sorted = useMemo(() => sortCameras(cameras, reference), [cameras, reference]);
  const matches = useMemo(() => filterCameras(sorted, query), [sorted, query]);

  useEffect(() => {
    if (!selected) return undefined;
    setImageState("loading");
    setImageVersion(Date.now());
    const timer = window.setInterval(() => setImageVersion(Date.now()), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [selected?.id]);

  function refresh() {
    setImageState("loading");
    setImageVersion(Date.now());
  }

  return (
    <div className="camera-panel">
      <div className="panel-intro">
        <h2>Cámaras de tráfico</h2>
        <p>Fotos de la DGT que se renuevan cada pocos minutos. No son vídeo en directo.</p>
      </div>

      {error && (
        <div className="notice is-error" role="alert">
          <span>{error}.</span>
          <button type="button" className="link-button" onClick={onRetry}>Reintentar</button>
        </div>
      )}

      {selected && (
        <article className="camera-viewer" aria-label={`Cámara ${selected.name}`}>
          <div className={`camera-frame is-${imageState}`}>
            {imageState === "error"
              ? <span>Esta cámara no ha enviado imagen. Prueba de nuevo en unos minutos.</span>
              : <img key={`${selected.id}-${imageVersion}`} src={`/api/camera-image?id=${encodeURIComponent(selected.id)}&t=${imageVersion}`} alt={`Foto de la DGT en ${selected.name}`} onLoad={() => setImageState("ready")} onError={() => setImageState("error")} />}
          </div>
          <div className="camera-meta">
            <div>
              <b>{selected.name}</b>
              <span>{selected.province || "España"} · {imageState === "ready" ? `foto consultada a las ${timeLabel(new Date(imageVersion))}` : imageState === "loading" ? "cargando foto…" : "sin imagen"}</span>
            </div>
            <button type="button" className="icon-button" onClick={refresh} aria-label="Pedir otra foto" title="Pedir otra foto"><Icon name="refresh" /></button>
          </div>
        </article>
      )}

      <div className="list-head">
        <h3>{loading ? "Cargando cámaras…" : `${matches.length.toLocaleString("es-ES")} cámaras`}</h3>
        <span>{reference ? `más cercanas a ${referenceLabel}` : "por carretera"}</span>
      </div>
      <label className="filter-field" htmlFor="camera-query">
        <Icon name="search" />
        <input id="camera-query" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Carretera o provincia, por ejemplo A-5" />
      </label>
      <ul className="camera-list">
        {matches.slice(0, 40).map((camera) => (
          <li key={camera.id}>
            <button type="button" className={camera.id === selected?.id ? "is-selected" : ""} onClick={() => onSelect(camera.id)} aria-pressed={camera.id === selected?.id}>
              <span className="camera-dot"><Icon name="camera" /></span>
              <span className="row-text"><span className="row-name">{camera.name}</span><span className="row-sub">{camera.province || "España"}</span></span>
              {Number.isFinite(camera.distanceFromKm) && <span className="camera-distance">{formatKm(camera.distanceFromKm)}</span>}
            </button>
          </li>
        ))}
      </ul>
      {matches.length > 40 && <p className="fine">Escribe una carretera o provincia para ver más.</p>}
      <p className="fine">La DGT no cubre todas las carreteras. Cataluña y el País Vasco publican sus cámaras en sus propios servicios.</p>
    </div>
  );
}
